import { z } from 'zod';
import { unwrap } from './unwrap';

/**
 * Determines if a given zod schema is required.
 */
export const getDefaultRequired = (schema: z.ZodTypeAny): boolean => {
    const unwrappedSchema = unwrap(schema);

    // booleans are not required by default, use z.literal<boolean>(true) for a required value
    if (unwrappedSchema instanceof z.ZodBoolean) {
        return false;
    }

    // if the union contains an empty string, it is not required
    if (
        unwrappedSchema instanceof z.ZodUnion &&
        unwrappedSchema._def.options.some(
            (option: z.ZodTypeAny) => option instanceof z.ZodLiteral && option._def.value === ''
        )
    ) {
        return false;
    }

    const notNullish =
        // z.default makes the schema optional, we need to read the inner (unwrapped) schema
        schema instanceof z.ZodDefault
            ? !unwrappedSchema.isNullable() && !unwrappedSchema.isOptional()
            : !schema.isNullable() && !schema.isOptional();

    // required zod string needs a minimum length > 0
    if (unwrappedSchema instanceof z.ZodString) {
        return notNullish && (unwrappedSchema.minLength ?? 0) > 0;
    }

    // required zod array needs a minimum length > 0
    if (unwrappedSchema instanceof z.ZodArray) {
        return notNullish && (unwrappedSchema._def.minLength?.value ?? 0) > 0;
    }

    return notNullish;
};
